import { getAuth } from 'firebase/auth'

import { ApiCallParameters, ApiResponse, OuterApiResponse } from '../_types/api'

const DEV_BYPASS_FLAG_STORAGE_KEY = 'quickpass:dev:bypassAuth'

/**
 * Pulls a fresh Firebase Auth ID token for the currently signed-in renter.
 * Returns null when no Firebase Auth user is present — callers should still
 * make the request, the ASG will reject any command that requires a UID.
 *
 * @returns {Promise<string | null>}
 */
const getAuthorizationToken = async (): Promise<string | null> => {
  try {
    const currentUser = getAuth().currentUser

    if (currentUser === null) {
      return null
    }

    return await currentUser.getIdToken()
  } catch (error) {
    console.warn('[api-call] failed to fetch ID token', error)
    return null
  }
}

/**
 * Returns true when a real backend gateway URL is configured AND the dev
 * auth bypass is NOT active. The bypass renter lives entirely in
 * localStorage — hitting a real backend would 404 every read and
 * pollute Firestore on writes.
 *
 * @returns {boolean}
 */
const isBackendConfigured = (): boolean => {
  const value = (import.meta.env.VITE_API_URL ?? '').trim()

  if (value === '') {
    return false
  }

  if (import.meta.env.DEV === true) {
    try {
      if (window.localStorage.getItem(DEV_BYPASS_FLAG_STORAGE_KEY) === '1') {
        return false
      }
    } catch (error) {
      console.warn('[api-call] failed to read bypass flag', error)
    }
  }

  return true
}

/**
 * Posts a command to the QuickCasa api-server-gateway. Every QuickPass
 * backend call flows through here — components and hooks must NEVER call
 * `fetch` directly. ASG accepts the callable envelope shape:
 * `{ data: { group, command, ...rest } }`. It responds with
 * `{ result: { data: ApiResponse } }`. CORS must be enabled on the gateway
 * (`cors: true` in api-server-gateway.ts) for the browser preflight to
 * succeed; otherwise the call fails before it leaves the browser.
 *
 * @param {ApiCallParameters} parameters
 * @returns {Promise<ApiResponse>}
 */
const apiCall = async (parameters: ApiCallParameters): Promise<ApiResponse> => {
  if (isBackendConfigured() === false) {
    throw new Error(
      'QuickPass api-server-gateway URL is not configured (set VITE_API_URL in .env.local). Falling back to local storage.',
    )
  }

  const baseUrl = (import.meta.env.VITE_API_URL ?? '').trim()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  const idToken = await getAuthorizationToken()
  if (idToken !== null) {
    headers.Authorization = `Bearer ${idToken}`
  }

  const response = await fetch(baseUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      data: {
        group: parameters.group,
        command: parameters.command,
        ...(parameters.data ?? {}),
      },
    }),
  })

  if (!response.ok) {
    throw new Error(
      `api-server-gateway responded with HTTP ${response.status} — ${response.statusText}`,
    )
  }

  const json = (await response.json()) as OuterApiResponse

  return json.result.data
}

export { apiCall, isBackendConfigured }
